import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Download, RefreshCw, CheckCircle, XCircle, Clock, MessageCircle, Eye, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatchConfirmation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [responses, setResponses] = useState(null);
  const [responseFilter, setResponseFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('send');

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  const [formData, setFormData] = useState({
    magazine: 'Aabkari Times',
    month: '',
    year: new Date().getFullYear().toString(),
    courier_type: '',
    dispatch_date: '',
    template_sid: localStorage.getItem('dispatch_confirm_sid') || 'HX63b2f9a7f6d8968b0369435e27e81e88'
  });

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (formData.template_sid) {
      localStorage.setItem('dispatch_confirm_sid', formData.template_sid);
    }
  }, [formData.template_sid]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dispatch-confirmation/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(res.data);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const handlePreview = async () => {
    if (!formData.magazine || !formData.month || !formData.year) {
      toast.error('Please select Magazine, Month, and Year');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/dispatch-confirmation/preview`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(res.data);
      const allIds = new Set(res.data.eligible.map(r => r.member_id));
      setSelectedMembers(allIds);
      setSelectAll(true);
    } catch (err) {
      console.error('Preview error:', err);
      toast.error(err.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoading(false);
    }
  };

  const toggleMember = (memberId) => {
    const next = new Set(selectedMembers);
    if (next.has(memberId)) {
      next.delete(memberId);
    } else {
      next.add(memberId);
    }
    setSelectedMembers(next);
    setSelectAll(next.size === (previewData?.eligible?.length || 0));
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(previewData?.eligible?.map(r => r.member_id) || []));
    }
    setSelectAll(!selectAll);
  };

  const handleSend = async () => {
    if (selectedMembers.size === 0) {
      toast.error('Please select at least one member');
      return;
    }
    if (!formData.template_sid) {
      toast.error('Template SID is required');
      return;
    }

    if (!window.confirm(`Send dispatch confirmation WhatsApp to ${selectedMembers.size} members?`)) {
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        member_ids: Array.from(selectedMembers)
      };
      const res = await axios.post(`${API}/dispatch-confirmation/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });

      const data = res.data;
      toast.success(`Sent: ${data.sent} | Failed: ${data.failed} | Skipped: ${data.skipped}`);
      setPreviewData(null);
      fetchCampaigns();
      setActiveTab('responses');
    } catch (err) {
      console.error('Send error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send confirmations');
    } finally {
      setSending(false);
    }
  };

  const viewResponses = async (campaign) => {
    setSelectedCampaign(campaign);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const filter = responseFilter === 'all' ? '' : `?response_filter=${responseFilter}`;
      const res = await axios.get(`${API}/dispatch-confirmation/responses/${campaign.id}${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponses(res.data);
    } catch (err) {
      console.error('Error fetching responses:', err);
      toast.error('Failed to fetch responses');
    } finally {
      setLoading(false);
    }
  };

  const refreshResponses = () => {
    if (selectedCampaign) viewResponses(selectedCampaign);
  };

  useEffect(() => {
    if (selectedCampaign) viewResponses(selectedCampaign);
  }, [responseFilter]);

  const exportToExcel = async () => {
    if (!selectedCampaign) return;
    try {
      const token = localStorage.getItem('token');
      const filter = responseFilter === 'all' ? '' : `?response_filter=${responseFilter}`;
      const res = await axios.get(`${API}/dispatch-confirmation/export/${selectedCampaign.id}${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = res.data.data;
      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }

      const headers = ['Member ID', 'Name', 'Phone', 'Magazine', 'Month', 'Year', 'Courier', 'Dispatch Date', 'Response', 'Response At'];
      const csvContent = [
        headers.join(','),
        ...data.map(r => [
          r.member_id || '',
          `"${r.name || ''}"`,
          r.phone || '',
          r.magazine || '',
          r.month || '',
          r.year || '',
          deliveryLabel(r.courier_type) || '',
          r.dispatch_date || '',
          r.response || 'Pending',
          r.response_at ? new Date(r.response_at).toLocaleString('en-IN') : ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const filterLabel = responseFilter === 'all' ? 'All' : responseFilter.charAt(0).toUpperCase() + responseFilter.slice(1);
      link.download = `Dispatch_Confirmation_${filterLabel}_${selectedCampaign.month}_${selectedCampaign.year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Report exported!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} /> Back
          </Button>
          <h1 className="text-3xl font-bold text-green-800" data-testid="page-title">
            Dispatch Confirmation (WhatsApp)
          </h1>
        </div>

        <div className="flex space-x-2 mb-6">
          <Button variant={activeTab === 'send' ? 'default' : 'outline'} onClick={() => { setActiveTab('send'); setPreviewData(null); }} data-testid="tab-send">
            <Send size={16} className="mr-2" /> Send Confirmations
          </Button>
          <Button variant={activeTab === 'responses' ? 'default' : 'outline'} onClick={() => setActiveTab('responses')} data-testid="tab-responses">
            <MessageCircle size={16} className="mr-2" /> View Responses ({campaigns.length})
          </Button>
        </div>

        {/* Send Tab */}
        {activeTab === 'send' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-green-700 mb-4">Send Dispatch Confirmation</h2>
            <p className="text-sm text-gray-500 mb-6">Send WhatsApp Yes/No confirmation to dispatched members. Members who already received a message for the same month won't be sent again.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Magazine *</Label>
                <Select value={formData.magazine} onValueChange={(v) => { setFormData({ ...formData, magazine: v }); setPreviewData(null); }}>
                  <SelectTrigger data-testid="magazine-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Month *</Label>
                <Select value={formData.month} onValueChange={(v) => { setFormData({ ...formData, month: v }); setPreviewData(null); }}>
                  <SelectTrigger data-testid="month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                  <SelectContent>
                    {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year *</Label>
                <Select value={formData.year} onValueChange={(v) => { setFormData({ ...formData, year: v }); setPreviewData(null); }}>
                  <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Courier Type (Optional)</Label>
                <Select value={formData.courier_type || 'all_couriers'} onValueChange={(v) => { setFormData({ ...formData, courier_type: v === 'all_couriers' ? '' : v }); setPreviewData(null); }}>
                  <SelectTrigger data-testid="courier-select"><SelectValue placeholder="All Couriers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_couriers">All Couriers</SelectItem>
                    {DELIVERY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Dispatch Date (Optional)</Label>
                <Input type="date" value={formData.dispatch_date} onChange={(e) => { setFormData({ ...formData, dispatch_date: e.target.value }); setPreviewData(null); }} data-testid="dispatch-date-input" />
              </div>

              <div className="space-y-2">
                <Label>Content Template SID *</Label>
                <Input value={formData.template_sid} onChange={(e) => setFormData({ ...formData, template_sid: e.target.value })} placeholder="HXxxxxxxxxx" data-testid="template-sid-input" />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
              <p className="font-semibold mb-1">Template Preview:</p>
              <p className="text-gray-600 italic">
                Dear Reader, Your {formData.magazine} <strong>{formData.month || '{{month}}'}</strong> month issue
                (Dispatch ID: <strong>{'{{member_id}}'}</strong>) was sent via Courier: <strong>{'{{courier}}'}</strong> on
                Date: <strong>{formData.dispatch_date || '{{date}}'}</strong>. Have you received it? [Yes] [No]
              </p>
            </div>

            <div className="flex space-x-3 mb-6">
              <Button onClick={handlePreview} disabled={loading} variant="outline" className="border-green-500 text-green-700 hover:bg-green-50" data-testid="preview-button">
                <Eye size={16} className="mr-2" />
                {loading ? 'Loading...' : 'Preview Recipients'}
              </Button>
              {previewData && selectedMembers.size > 0 && (
                <Button onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-700 text-white" data-testid="send-button">
                  <Send size={16} className="mr-2" />
                  {sending ? 'Sending...' : `Send to ${selectedMembers.size} Members`}
                </Button>
              )}
            </div>

            {/* Preview Results */}
            {previewData && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <Users size={20} className="mr-2 text-green-600" />
                    Recipient Preview
                  </h3>
                  <div className="flex space-x-4 text-sm">
                    <span className="text-green-600 font-medium">Eligible: {previewData.eligible?.length || 0}</span>
                    <span className="text-yellow-600 font-medium">Already Sent: {previewData.already_sent?.length || 0}</span>
                    <span className="text-gray-500">No Phone: {previewData.no_phone?.length || 0}</span>
                  </div>
                </div>

                {previewData.already_sent?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4 text-sm">
                    <p className="font-semibold text-yellow-700">Already sent ({previewData.already_sent.length}):</p>
                    <p className="text-yellow-600 mt-1">{previewData.already_sent.map(r => r.member_id).join(', ')}</p>
                  </div>
                )}

                {previewData.eligible?.length > 0 && (
                  <>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 mr-2 accent-green-600"
                        data-testid="select-all-checkbox"
                      />
                      <Label className="text-sm cursor-pointer" onClick={toggleSelectAll}>Select All ({previewData.eligible.length})</Label>
                    </div>
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="p-2 text-left w-10"></th>
                            <th className="p-2 text-left font-semibold">Member ID</th>
                            <th className="p-2 text-left font-semibold">Name</th>
                            <th className="p-2 text-left font-semibold">Phone</th>
                            <th className="p-2 text-left font-semibold">Courier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.eligible.map((r, idx) => (
                            <tr key={idx} className={`border-t hover:bg-gray-50 ${selectedMembers.has(r.member_id) ? 'bg-green-50' : ''}`}>
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={selectedMembers.has(r.member_id)}
                                  onChange={() => toggleMember(r.member_id)}
                                  className="h-4 w-4 accent-green-600"
                                  data-testid={`select-${r.member_id}`}
                                />
                              </td>
                              <td className="p-2 font-mono text-xs">{r.member_id}</td>
                              <td className="p-2">{r.name}</td>
                              <td className="p-2 text-xs">{r.phone}</td>
                              <td className="p-2">{deliveryLabel(r.courier_type)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {previewData.eligible?.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No eligible recipients found. All members may have already been sent a confirmation for this month.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Responses Tab */}
        {activeTab === 'responses' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-green-700 mb-4">Confirmation Campaigns</h2>

            {campaigns.length === 0 ? (
              <p className="text-gray-500">No campaigns sent yet.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {campaigns.map(c => (
                    <div
                      key={c.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedCampaign?.id === c.id ? 'border-green-500 bg-green-50 ring-2 ring-green-200' : 'hover:border-gray-400'}`}
                      onClick={() => viewResponses(c)}
                      data-testid={`campaign-${c.id}`}
                    >
                      <h4 className="font-semibold text-sm">{c.magazine} - {c.month} {c.year}</h4>
                      <p className="text-xs text-gray-500 mt-1">Sent: {new Date(c.sent_at).toLocaleString('en-IN')}</p>
                      <div className="flex space-x-3 mt-2 text-xs">
                        <span className="text-green-600 font-medium">Sent: {c.sent_count}</span>
                        <span className="text-red-600 font-medium">Failed: {c.failed_count}</span>
                        <span className="text-gray-500">Skipped: {c.skipped_count}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedCampaign && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        Responses: {selectedCampaign.magazine} - {selectedCampaign.month} {selectedCampaign.year}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Select value={responseFilter} onValueChange={setResponseFilter}>
                          <SelectTrigger className="w-40" data-testid="response-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Responses</SelectItem>
                            <SelectItem value="yes">Received (Yes)</SelectItem>
                            <SelectItem value="no">Not Received (No)</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={refreshResponses} data-testid="refresh-btn"><RefreshCw size={16} /></Button>
                        <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-btn"><Download size={16} className="mr-1" /> Export</Button>
                      </div>
                    </div>

                    {responses?.summary && (
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">{responses.summary.total}</div>
                          <div className="text-xs text-gray-500">Total Sent</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{responses.summary.yes}</div>
                          <div className="text-xs text-gray-500">Received (Yes)</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <div className="text-2xl font-bold text-red-600">{responses.summary.no}</div>
                          <div className="text-xs text-gray-500">Not Received (No)</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">{responses.summary.pending}</div>
                          <div className="text-xs text-gray-500">Pending</div>
                        </div>
                      </div>
                    )}

                    {loading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="p-2 text-left font-semibold">Member ID</th>
                              <th className="p-2 text-left font-semibold">Name</th>
                              <th className="p-2 text-left font-semibold">Phone</th>
                              <th className="p-2 text-left font-semibold">Courier</th>
                              <th className="p-2 text-left font-semibold">Dispatch Date</th>
                              <th className="p-2 text-center font-semibold">Response</th>
                              <th className="p-2 text-left font-semibold">Response At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {responses?.responses?.map((r, idx) => (
                              <tr key={idx} className="border-t hover:bg-gray-50">
                                <td className="p-2 font-mono text-xs">{r.member_id}</td>
                                <td className="p-2">{r.name}</td>
                                <td className="p-2 text-xs">{r.phone}</td>
                                <td className="p-2">{deliveryLabel(r.courier_type)}</td>
                                <td className="p-2">{r.dispatch_date}</td>
                                <td className="p-2 text-center">
                                  {r.response === 'yes' && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold"><CheckCircle size={12} className="mr-1" />Yes</span>}
                                  {r.response === 'no' && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold"><XCircle size={12} className="mr-1" />No</span>}
                                  {!r.response && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold"><Clock size={12} className="mr-1" />Pending</span>}
                                </td>
                                <td className="p-2 text-xs">{r.response_at ? new Date(r.response_at).toLocaleString('en-IN') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {responses?.responses?.length === 0 && (
                          <p className="text-center text-gray-400 py-4">No responses matching the filter.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DispatchConfirmation;
